import * as ctHelper from '@/helpers/contractHelpers'
import { defineStore } from 'pinia'
import { IOptionList } from "@/interface"
import { IContract, ILead } from "@/interface/services/response/contract.interface"
import { getAuthenticationStore, getCommonStore, getListStore, getProductStore, getPromotionStore } from '@/helpers/storeHelpers'

export const useContractStore = defineStore('contract', {
    state: (): IContractState => ({
        contracts: [],
        lastUpdate: null,
        selectedContract: {
            index: -1,
            id: 0
        }
    }),
    actions: {
        initStoreWithValue(contracts: IContract[]) {
            this.contracts = []

            contracts.forEach(c => {
                const ct = new StoreContract({
                    id: c.id,
                    contractId: c.contract_id,
                    orderId: c.order_id,
                    scenario: c.scenario,
                    msisdn: c.msisdn,
                    status: c.status.contractStatus,
                    subStatus: c.status.contractSubStatus,
                    idCardNumber: c.idCardNumber,
                    simIccid: c.simCard.ICCID,
                    simIccidOther: c.portIn.ICCIDOther,
                    simSentAt: c.simCard.sentAt,
                    street: c.address.street,
                    streetNumber: c.address.number,
                    box: c.address.box,
                    city: c.address.city,
                    zipCode: c.address.zipCode,
                    country: c.address.country,
                    activeProducts: [] as Array<IContractProduct>,
                    inactiveProducts: [] as Array<IContractProduct>,
                    lead: c.lead ?? null,
                    createdAt: c.createdAt,
                    updatedAt: c.updatedAt,
                    activatedAt: c.simCard.activatedAt,
                    activatedMegaAt: c.simCard.megaActivatedAt
                })

                if (c.products) {
                    c.products.forEach(contractProduct => {
                        const product = {
                            id: contractProduct.id,
                            isBaseProduct: contractProduct.isBaseProduct,
                            billCycleId: contractProduct.costCenter?.billCycleId ?? undefined,
                            dunningLevel: contractProduct.costCenter?.dunningLevel ?? undefined
                        } as IContractProduct
                        if (contractProduct.isActive) {
                            ct.activeProducts.push(product)
                        } else {
                            ct.inactiveProducts.push(product)
                        }
                    });
                }
                
                this.contracts.push(ct)
            })

            if (this.contracts.length) {
                let ct = -1;
                const authStore = getAuthenticationStore()
                const isUser = authStore?.isAuthenticated && authStore?.isUser
                if (isUser && authStore.msisdn) {
                    ct = this.contracts.findIndex(e => e.msisdn === authStore.msisdn)
                } else if (this.currentContract == null) {
                    const displayableContracts = this.contracts.filter(e => ctHelper.isDisplayable(e));
                    const dates = displayableContracts.map(e => {
                        return new Date(e.createdAt).getTime();
                    })

                    const earliest = Math.min(...dates);
                    if (displayableContracts) {
                        let contract: StoreContract|null|undefined = null
                        const commonStore = getCommonStore()
                        const forcedContract = commonStore?.forceContractId ?? null
                        if (forcedContract) {
                            contract = displayableContracts.find(e => e.id === forcedContract)
                        } else {
                            contract = displayableContracts.find(e => e.msisdn && new Date(e.createdAt).getTime() === earliest)
                        }
                        if (!contract) {
                            contract = displayableContracts.find(e => e.id > 0)
                        }
                        if (contract && contract.id) {
                            ct = this.contracts.findIndex(e => e.id == contract?.id)
                        }
                    }
                }
                if (ct != -1) {
                    this.setCurrentContract(ct)
                }
            }

            this.synchronizeList()
            this.resetForcedContract()
            this.lastUpdate = new Date()
        },
        existContract(id: number) : boolean {
            if (!this.contracts.length) {
                return false
            }
            return this.contracts.find(e => e.id === id) != undefined
        },
        findContract(id: number) {
            return new StoreContract(this.contracts[this.contracts.findIndex(e => e.id == id)])
        },
        resetForcedContract() {
            const commonStore = getCommonStore()
            if (commonStore) {
                commonStore.forceContractId = null
            }
        },
        selectContract(id: number) {
            const ct = this.contracts.findIndex(e => e.id === id)
            if (ct != -1) { 
                this.setCurrentContract(ct)
            }
        },
        setCurrentContract(index: number) {
            this.selectedContract.index = index 
            this.selectedContract.id = this.contracts[index].id
        },
        synchronizeList() {
            const currentCt = this.currentContract
            const listStore = getListStore()
            if (currentCt && listStore) {
                listStore.updateSelectedMsisdn(this.msisdnList?.find(e => {
                    return (e.value == "" + currentCt.id)
                }) ?? { label: "", value: "0" })
            }
        },
        updateContractStatus(id: number, status: string, subStatus: string|null) {
            const index = this.contracts.findIndex(e => e.id === id)
            if (index !== -1) {
                this.contracts[index].status = status
                this.contracts[index].subStatus = subStatus
            }
        }
    },
    getters: {
        currentId(_state: IContractState) : number|null {
          return this.currentContract?.id ?? null
        },
        currentBillCycle(_state: IContractState) : number|null {
            if (!this.currentContract) {
                return null
            }

            return this.currentContract.getBaseBillCycleId()
        },
        currentContract(state: IContractState) : StoreContract|null {
            if (state.selectedContract.index == -1 || state.selectedContract.id == 0) {
                return null
            }
            const ct = state.contracts[state.selectedContract.index]
            if (ct.id != state.selectedContract.id) {
                state.selectedContract.index = state.contracts.findIndex(e => e.id == ct.id)
            }

            return new StoreContract(state.contracts[state.selectedContract.index])
        },
        currentIccid(_state: IContractState) : string|null {
            if (!this.currentContract || !this.currentContract.simIccid || this.currentContract.simIccid == '') {
                return null
            }

            return this.currentContract.simIccid
        },
        contractIds(state: IContractState) : number[]|null {
            if (!state.contracts || state.contracts.length == 0) {
                return null
            }

            return this.contracts.map(e => e.id)
        },
        currentDunningLevel(_state: IContractState) : number {
            if (!this.currentContract || this.currentContract.activeProducts.length == 0) {
                return 0
            }

            return this.currentContract.activeProducts.find(e => e.isBaseProduct == true)?.dunningLevel ?? 0
        },
        currentDunningLevelBlocks(_state: IContractState) : boolean {
            return this.currentDunningLevel >= 2
        },
        currentMsisdn(_state: IContractState) : string|null {
            if (!this.currentContract || this.currentContract.msisdn == '') {
                return null
            }

            return this.currentContract.msisdn
        },
        currentOptionsIds(_state: IContractState) : number[] {
            if (!this.currentContract) {
                return []
            }

            return this.currentContract.getActiveOptionsIds()
        },
        currentProductBaseId(_state: IContractState) : number|null {
            if (!this.currentContract) {
                return null
            }

            return this.currentContract.getActiveBaseProduct()
        },
        hasAtLeastOneBlockage(_state: IContractState) : boolean {
            return this.isStatusBlocked || this.isStatusBlockedFraud || this.isStatusBlockedCredit
        },
        hasSubStatus(_state: IContractState) : boolean {
            if (this.currentContract && this.currentContract.subStatus && this.currentContract.subStatus != '') {
                return true
            }

            return false
        },
        hasAtLeastOneSimActivable(state: IContractState) : boolean {
            const ct = state.contracts.find(e => ctHelper.isSimActivable(e) && ctHelper.isDisplayable(e))
            if (ct) {
                return true 
            }

            return false
        },
        hasAtLeastOneSimActivated(state: IContractState) : boolean {
            const ct = state.contracts.find(e => ctHelper.isContractActivated(e))
            if (ct) {
                return true 
            }

            return false
        },
        isSimActivable(_state: IContractState) : boolean {
            return ctHelper.isSimActivable(this.currentContract)
        },
        isStatusActivated(_state: IContractState): boolean {
            return ctHelper.isContractActivated(this.currentContract)
        },
        isStatusCancellable(_state: IContractState) : boolean {
            return ctHelper.isCancellable(this.currentContract)
        },
        isStatusBlocked(_state: IContractState) : boolean {
            return ctHelper.isBlocked(this.currentContract)
        },
        isStatusBlockedCredit(_state: IContractState) : boolean {
            return ctHelper.isBlockedCredit(this.currentContract)
        },
        isStatusBlockedFraud(_state: IContractState) : boolean {
            return ctHelper.isBlockedFraud(this.currentContract)
        },
        msisdnList(state: IContractState) : IOptionList[]|null {
            if (state.contracts.length == 0) {
                return null
            }

            const authStore = getAuthenticationStore()
            const isUser = authStore?.isAuthenticated && authStore?.isUser
            if (isUser && authStore.msisdn) {
                return null
            }

            const array = [] as IOptionList[]
            const displayableContracts = state.contracts.filter(e => ctHelper.isDisplayable(e))
            if (displayableContracts) {
                displayableContracts.sort((a, b) => {
                    if (!a.msisdn || a.msisdn == '') {
                        return 1
                    }
                    if (a.msisdn == b.msisdn) {
                        return 0
                    }

                    return -1
                }).forEach(e => {
                    array.push({ 
                        label: ctHelper.getDisplayId(e), 
                        value: "" + e.id,
                        meta: {
                            hasMsisdn: (e.msisdn && e.msisdn !== '') ?? false,
                            hasContractId: (e.contractId && e.contractId !== '') ?? false
                        }
                    })
                })
            }
            if (array.length == 0) {
                return null
            }

            return array
        },
        statusI18n(_state: IContractState) : string {
          if (!this.currentContract) {
              return "subscription.status.unknown"
          }

          return this.currentContract.status ? `subscription.status.${this.currentContract.status}` : '';
        },
        subStatusI18n(_state: IContractState) : string {
            if (!this.currentContract || !this.currentContract.subStatus) {
                return "subscription.subStatus.unknown"
            }

            return `subscription.subStatus.${this.currentContract.subStatus}`
        },
        shouldRedirectToOrderFollow(_state: IContractState) : boolean {
            return ctHelper.statusRedirectsToOrderFollow(this.currentContract)
        },
        totalPrice(_state: IContractState) : number|null {
            if (!this.currentContract) {
                return null
            }

            const productStore = getProductStore()
            const promotionStore = getPromotionStore()
            if (!productStore || !productStore.baseProduct || productStore.baseProduct.id !== this.currentProductBaseId) {
                return null
            }

            const productPrice = productStore.price;
            const optionsPrice = productStore.priceOptions;
            const promotionsTotal = promotionStore?.totals || 0;

            return promotionsTotal ?
                (productPrice * promotionsTotal?.percentage?.factor ?? 1) + optionsPrice - promotionsTotal?.amount?.quantity ?? 0 :
                productPrice + optionsPrice;
        }
    },
    persist: true,
})

interface IContractState {
    contracts: StoreContract[],
    lastUpdate: Date|null,
    selectedContract: {
        index: number,
        id: number,
    },
}

export interface IContractProduct {
    id: number,
    isBaseProduct: boolean,
    billCycleId?: number
    dunningLevel?: number
}

export interface IStoreContract {
    id: number,
    contractId: string|null,
    orderId: string|null,
    scenario: string|null,
    msisdn: string,
    status: string,
    subStatus: string|null,
    idCardNumber: string,
    simIccid: string|null,
    simIccidOther: string|null,
    simSentAt: string|null,
    street: string|null,
    streetNumber: string|null,
    box: string|null,
    city: string|null,
    zipCode: string|null,
    country: string|null,
    activeProducts: Array<IContractProduct>,
    inactiveProducts: Array<IContractProduct>,
    lead: ILead|null,
    createdAt: string,
    updatedAt: string|null,
    activatedAt: string|null,
    activatedMegaAt: string|null
}

export class StoreContract implements IStoreContract {
    id = 0
    contractId: string|null = null
    orderId: string|null = null
    scenario: string|null = null
    msisdn = ''
    status = ''
    subStatus: string|null = null
    idCardNumber = ''
    simIccid: string|null = null
    simIccidOther: string|null = null
    simSentAt: string|null = null
    street: string|null = null
    streetNumber: string|null = null
    box: string|null = null
    city: string|null = null
    zipCode: string|null = null
    country: string|null = null
    activeProducts: Array<IContractProduct> = []
    inactiveProducts: Array<IContractProduct> = []
    createdAt = ''
    lead: ILead|null = null
    updatedAt: string|null = null
    activatedAt: string|null = null
    activatedMegaAt: string|null = null

    constructor(dto: IStoreContract) {
        Object.assign(this, dto)
    }

    public getActiveBaseProduct = () : number|null => {
        return this.activeProducts.find(e => e.isBaseProduct)?.id ?? null
    }

    public getActiveOptionsIds = () : number[] => {
        return this.activeProducts.filter(e => !e.isBaseProduct).map(e => e.id)
    }

    public getBaseBillCycleId = () : number|null => {
        return this.activeProducts.find(e => e.isBaseProduct)?.billCycleId ?? null
    }
}

export type ContractStore = ReturnType<typeof useContractStore>